import { Helmet } from 'react-helmet-async'
import { buildBreadcrumbSchema, SITE_NAME, SITE_URL } from '../../utils/seo.js'

const OG_IMAGE = `${SITE_URL}/og-image.png`

/**
 * LegalPage — minimal prose wrapper for policy / info pages.
 *
 * Props:
 *   title       string    – <title> tag
 *   description string    – meta description (required for SEO)
 *   slug        string    – path segment used to build canonical URL
 *   lastUpdated string    – "Month YYYY"
 *   children    ReactNode
 */
export default function LegalPage({ title, description, slug, lastUpdated, children }) {
  const canonical = slug ? `${SITE_URL}/${slug}` : undefined
  const fullTitle = `${title} – ${SITE_NAME}`

  return (
    <>
      <Helmet>
        <title>{fullTitle}</title>
        {description && <meta name="description" content={description} />}
        {canonical && <link rel="canonical" href={canonical} />}
        <meta property="og:title"       content={fullTitle} />
        <meta property="og:type"        content="website" />
        {canonical && <meta property="og:url" content={canonical} />}
        {description && <meta property="og:description" content={description} />}
        <meta property="og:image"       content={OG_IMAGE} />
        <meta name="twitter:card"       content="summary_large_image" />
        <meta name="twitter:title"      content={fullTitle} />
        {description && <meta name="twitter:description" content={description} />}
        <meta name="twitter:image"      content={OG_IMAGE} />
        {canonical && (
          <script type="application/ld+json">
            {JSON.stringify(buildBreadcrumbSchema(title, canonical))}
          </script>
        )}
      </Helmet>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 md:py-16">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2">{title}</h1>
        {lastUpdated && (
          <p className="text-sm text-muted-foreground mb-8">Last updated: {lastUpdated}</p>
        )}
        <div className="prose prose-sm dark:prose-invert max-w-none space-y-5 text-muted-foreground leading-relaxed">
          {children}
        </div>
      </div>
    </>
  )
}
